import { bytesToBase64Url, bytesFromBase64Url } from './utils';

const NFCRW_CALLBACK_STORAGE_KEY = 'nfcrwCallback';

// ネイティブアプリ側(nfcReaderWriter)と完全に一致させること
const NFCRW_HKDF_INFO_LABEL = 'monashell-nfcrw-v1';
const NFCRW_AES_GCM_IV_LENGTH = 12;

// セッション用の ECDH P-256 鍵ペアを生成する（公開鍵は raw 非圧縮 65 バイト）
async function generateNfcSessionKeyPair() {
  const keyPair = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    ['deriveBits'],
  );

  const browserPublicKey = new Uint8Array(await crypto.subtle.exportKey('raw', keyPair.publicKey));

  return { keyPair, browserPublicKey };
}

// ECDH の共有秘密（X 座標 32 バイト）から HKDF-SHA256 で AES-256-GCM 鍵を導出する
// info にラベルと両公開鍵を束縛し、公開鍵のすり替えを復号失敗として検出できるようにする
async function deriveNfcAesKey({ keyPair, browserPublicKey, appPublicKeyB64, usages }) {
  const appPublicKey = bytesFromBase64Url({ str: appPublicKeyB64 });
  const appKey = await crypto.subtle.importKey(
    'raw',
    appPublicKey,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );

  const sharedBits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: appKey },
    keyPair.privateKey,
    256,
  );

  const hkdfKey = await crypto.subtle.importKey('raw', sharedBits, 'HKDF', false, ['deriveKey']);

  const label = new TextEncoder().encode(NFCRW_HKDF_INFO_LABEL);
  const info = new Uint8Array(label.length + browserPublicKey.length + appPublicKey.length);

  info.set(label, 0);
  info.set(browserPublicKey, label.length);
  info.set(appPublicKey, label.length + browserPublicKey.length);

  const aesKey = await crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    usages,
  );

  return aesKey;
}

// records を AES-256-GCM で暗号化する
// 返り値は iv || 暗号文(GCMタグ含む) の base64url
async function encryptNfcRecords({ keyPair, browserPublicKey, appPublicKeyB64, records, state }) {
  const aesKey = await deriveNfcAesKey({ keyPair, browserPublicKey, appPublicKeyB64, usages: ['encrypt'] });

  const iv = crypto.getRandomValues(new Uint8Array(NFCRW_AES_GCM_IV_LENGTH));
  const plaintext = new TextEncoder().encode(JSON.stringify({ records }));
  const additionalData = new TextEncoder().encode(state);

  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData }, aesKey, plaintext)
  );

  const payload = new Uint8Array(iv.length + ciphertext.length);

  payload.set(iv, 0);
  payload.set(ciphertext, iv.length);

  return bytesToBase64Url({ bytes: payload });
}

// iv || 暗号文(GCMタグ含む) の base64url を AES-256-GCM で復号し、JSON を返す
async function decryptNfcRecords({ keyPair, browserPublicKey, appPublicKeyB64, ciphertextB64, state }) {
  const aesKey = await deriveNfcAesKey({ keyPair, browserPublicKey, appPublicKeyB64, usages: ['decrypt'] });

  const payload = bytesFromBase64Url({ str: ciphertextB64 });
  const iv = payload.slice(0, NFCRW_AES_GCM_IV_LENGTH);
  const ciphertext = payload.slice(NFCRW_AES_GCM_IV_LENGTH);
  const additionalData = new TextEncoder().encode(state);

  const plaintext = new Uint8Array(
    await crypto.subtle.decrypt({ name: 'AES-GCM', iv, additionalData }, aesKey, ciphertext)
  );

  return JSON.parse(new TextDecoder().decode(plaintext));
}

// ネイティブアプリへ鍵交換を依頼する Universal Link を組み立てる
function getNfcHandshakeUniversalLink({ browserPublicKeyB64, state, language }) {
  const returnUrl = 'https://' + process.env.REACT_APP_NFCRW_CALLBACK_URL;
  const url = new URL('https://' + process.env.REACT_APP_NFCRW_OPEN_ORIGIN + '/app/nfc/write');

  url.searchParams.set('mode', 'handshake');
  url.searchParams.set('return_url', returnUrl);
  url.searchParams.set('state', state);
  url.searchParams.set('pubkey', browserPublicKeyB64);
  url.searchParams.set('language', language);

  return url.toString();
}

// ブラウザ側の鍵ペアと state を作り、ハンドシェイク用 Universal Link を確定する
async function prepareNfcrwHandshake({ language }) {
  const session = await generateNfcSessionKeyPair();
  const state = crypto.randomUUID();
  const handshakeLink = getNfcHandshakeUniversalLink({
    browserPublicKeyB64: bytesToBase64Url({ bytes: session.browserPublicKey }),
    state,
    language,
  });

  return { session, state, handshakeLink };
}

// ネイティブアプリへ暗号化スキャンを依頼する Universal Link を組み立てる
function getNfcEncryptedScanUniversalLink({ browserPublicKeyB64, state, language }) {
  const returnUrl = 'https://' + process.env.REACT_APP_NFCRW_CALLBACK_URL;
  const url = new URL('https://' + process.env.REACT_APP_NFCRW_OPEN_ORIGIN + '/app/nfc/scan');

  url.searchParams.set('mode', 'encrypted');
  url.searchParams.set('return_url', returnUrl);
  url.searchParams.set('state', state);
  url.searchParams.set('pubkey', browserPublicKeyB64);
  url.searchParams.set('language', language);

  return url.toString();
}

// ブラウザ側の鍵ペアと state を作り、暗号化スキャン用 Universal Link を確定する
async function prepareNfcrwScan({ language }) {
  const session = await generateNfcSessionKeyPair();
  const state = crypto.randomUUID();
  const scanLink = getNfcEncryptedScanUniversalLink({
    browserPublicKeyB64: bytesToBase64Url({ bytes: session.browserPublicKey }),
    state,
    language,
  });

  return { session, state, scanLink };
}

// ネイティブアプリへ暗号化済みデータの NFC 書き込みを依頼する Universal Link を組み立てる
function getNfcEncryptedWriteUniversalLink({ ciphertextB64, state, language }) {
  const returnUrl = 'https://' + process.env.REACT_APP_NFCRW_CALLBACK_URL;
  const url = new URL('https://' + process.env.REACT_APP_NFCRW_OPEN_ORIGIN + '/app/nfc/write');

  url.searchParams.set('mode', 'encrypted');
  url.searchParams.set('return_url', returnUrl);
  url.searchParams.set('state', state);
  url.searchParams.set('ciphertext', ciphertextB64);
  url.searchParams.set('language', language);

  return url.toString();
}

// コールバックタブから元タブへ結果を渡す
function writeNfcrwCallbackToLocalStorage({ state, nfc, error, pubkey, ciphertext }) {
  localStorage.setItem(NFCRW_CALLBACK_STORAGE_KEY, JSON.stringify({ state, nfc, error, pubkey, ciphertext }));
}

// 元タブが結果を取得したら直後に消す
function readAndClearNfcrwCallbackFromLocalStorage({ state }) {
  const raw = localStorage.getItem(NFCRW_CALLBACK_STORAGE_KEY);

  if (!raw) {
    return null;
  }

  const payload = JSON.parse(raw);

  if (payload.state !== state) {
    return null;
  }

  localStorage.removeItem(NFCRW_CALLBACK_STORAGE_KEY);

  return payload;
}

// 消費できなくなったコールバックを捨てる
function clearNfcrwCallbackFromLocalStorage() {
  localStorage.removeItem(NFCRW_CALLBACK_STORAGE_KEY);
}

export {
  generateNfcSessionKeyPair,
  encryptNfcRecords,
  decryptNfcRecords,
  getNfcHandshakeUniversalLink,
  prepareNfcrwHandshake,
  getNfcEncryptedScanUniversalLink,
  prepareNfcrwScan,
  getNfcEncryptedWriteUniversalLink,
  writeNfcrwCallbackToLocalStorage,
  readAndClearNfcrwCallbackFromLocalStorage,
  clearNfcrwCallbackFromLocalStorage,
};
